import { gql } from '@apollo/client';
import type { TypedDocumentNode } from '@apollo/client';
import React, { useState } from 'react';
import type { CSSProperties } from 'react';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { borderGradientStyle } from '@sorare/core/src/style/utils';

import { PackWithBuyButton } from 'components/commonCard/BuyPacks/PackWithBuyButton';
import { StarPackContent } from 'components/commonCard/BuyPacks/StarPack/StarPackContent';
import { StarPackTitle } from 'components/commonCard/BuyPacks/StarPack/StarPackTitle';
import { usePackAssets } from 'components/commonCard/PacksTypePage/usePackAssets';

import { MoveToElitePackButton } from './MoveToElitePackButton';
import { MoveToStarPackButton } from './MoveToStarPackButton';
import type {
  ScrollableHighlightedPacks_cardPack,
  ScrollableHighlightedPacks_cardPackGroup,
} from './__generated__/index.graphql';
import { PremiumPackTitle } from '../../PremiumPack/PremiumPackTitle';

const Root = styled(Vertical).attrs({ gap: 3, center: true })`
  position: relative;
  padding: var(--double-unit);
  width: 100%;
  background:
    linear-gradient(to bottom, transparent 0%, var(--c-black) 100%),
    var(--background-image) bottom -10% center/cover no-repeat;
  border-radius: var(--double-unit);
  --border-gradient: linear-gradient(
    to bottom,
    var(--league-color) 0%,
    transparent 20%
  );
  ${borderGradientStyle};
  overflow: hidden;
  justify-content: space-between;
`;

const StyledNeonEdge = styled(NeonEdge)`
  position: absolute;
  top: -1px;
  height: var(--double-unit);
  width: 200%;
  transform: rotate(180deg);
`;

type Props = {
  starCardPack: Nullable<ScrollableHighlightedPacks_cardPack>;
  premiumCardPack: Nullable<ScrollableHighlightedPacks_cardPack>;
  cardPackGroup: ScrollableHighlightedPacks_cardPackGroup;
  onClickBuy: (cardPackGroupId: string, cardPackSlug: string) => void;
};

export const ScrollableHighlightedPacks = ({
  starCardPack,
  premiumCardPack,
  cardPackGroup,
  onClickBuy,
}: Props) => {
  const [visibleItem, setVisibleItem] = useState<'premium_pack' | 'star_pack'>(
    'premium_pack'
  );

  const updateVisibleItem = (item: 'premium_pack' | 'star_pack') => {
    setVisibleItem(item);
  };
  const { bgImage } = usePackAssets({
    cardPackGroupId: idFromObject(cardPackGroup.id),
  });
  const premiumPackContent = premiumCardPack ? (
    <PackWithBuyButton
      cardPack={premiumCardPack}
      cardPackGroupId={cardPackGroup.id}
      onClickBuy={onClickBuy}
    />
  ) : null;
  let content = null;
  if (starCardPack && premiumCardPack) {
    content = (
      <>
        <Scrollable
          itemToDisplay={1}
          indexToScroll={visibleItem === 'star_pack' ? 1 : 0}
          withScrollButtons={false}
          onVisibleItemsChanged={items => {
            if (items[0] === 1) {
              updateVisibleItem('star_pack');
            } else {
              updateVisibleItem('premium_pack');
            }
          }}
        >
          {premiumPackContent}
          <StarPackContent cardPack={starCardPack} />
        </Scrollable>
        {visibleItem === 'star_pack' ? (
          <MoveToElitePackButton
            cardPack={premiumCardPack}
            onClick={() => {
              updateVisibleItem('premium_pack');
            }}
          />
        ) : (
          <MoveToStarPackButton
            cardPack={starCardPack}
            onClick={() => {
              updateVisibleItem('star_pack');
            }}
          />
        )}
      </>
    );
  } else {
    content = premiumPackContent;
  }
  return (
    <Root
      style={
        {
          '--league-color': premiumCardPack?.primaryColor,
          '--background-image': `url(${bgImage})`,
        } as CSSProperties
      }
    >
      <StyledNeonEdge
        color={premiumCardPack?.primaryColor ?? ''}
        showHalo="always"
        showNeon="always"
      />
      {visibleItem === 'premium_pack'
        ? premiumCardPack && (
            <PremiumPackTitle
              cardPackGroup={cardPackGroup}
              cardPack={premiumCardPack}
            />
          )
        : starCardPack && <StarPackTitle cardPack={starCardPack} />}
      {content}
    </Root>
  );
};

ScrollableHighlightedPacks.fragments = {
  cardPack: gql`
    fragment ScrollableHighlightedPacks_cardPack on CardPack {
      slug
      primaryColor: color(variant: PRIMARY)
      ...StarPackContent_cardPack
      ...PackWithBuyButton_cardPack
      ...MoveToStarPackButton_cardPack
      ...MoveToElitePackButton_cardPack
      ...StarPackTitle_cardPack
      ...PremiumPackTitle_cardPack
    }
    ${StarPackContent.fragments.cardPack}
    ${PackWithBuyButton.fragments.cardPack}
    ${MoveToStarPackButton.fragments.cardPack}
    ${MoveToElitePackButton.fragments.cardPack}
    ${StarPackTitle.fragments.cardPack}
    ${PremiumPackTitle.fragments.cardPack}
  ` as TypedDocumentNode<ScrollableHighlightedPacks_cardPack>,
  cardPackGroup: gql`
    fragment ScrollableHighlightedPacks_cardPackGroup on CardPackGroup {
      id
      ...PremiumPackTitle_cardPackGroup
    }
    ${PremiumPackTitle.fragments.cardPackGroup}
  ` as TypedDocumentNode<ScrollableHighlightedPacks_cardPackGroup>,
};
